import { mutateByPath } from "../../../utility";
import { ARSTActionSetScaffoldRowHeight, ArsTableState } from "../interface";

/**
 * Updates the height of a scaffold row in both the scaffold model and body data.
 *
 * @param state - Current ARS table state
 * @param action - Height update action for a scaffold row
 * @returns Updated ARS table state
 */
export const arst_setScaffoldRowHeight = <T>(
  state: ArsTableState<T>,
  action: ARSTActionSetScaffoldRowHeight,
): ArsTableState<T> => {
  if (!state.scaffoldModel) return state;

  const rowIndex = state.scaffoldModel.rows.findIndex((row) => row.id === action.rowId);
  if (rowIndex === -1) return state;

  const scaffoldRow = state.scaffoldModel.rows[rowIndex];
  const newScaffoldModel = mutateByPath(
    state.scaffoldModel,
    `/rows/${rowIndex}/height`,
    action.height,
  );

  let newBodyData = { ...state.bodyData };
  if (scaffoldRow.type === "header") {
    const newHeights = state.scaffoldModel.rows
      .filter((row) => row.type === "header")
      .map((row) => (row.id === action.rowId ? action.height : row.height));
    newBodyData = mutateByPath(
      state.bodyData,
      `/rowHeaders`,
      state.bodyData.rowHeaders.map((rowHeader) => ({ ...rowHeader, heights: newHeights })),
    );
  } else {
    const targetRow = Object.values(
      state.scaffoldModel.cellData[action.rowId] ??
        // istanbul ignore next
        {},
    );
    const targetCell = targetRow.find((cell) => !("rowSpan" in cell) || (cell.rowSpan ?? 1) === 1);
    /* istanbul ignore else */
    if (targetCell) {
      const pathSegments = targetCell.dataPath.split("/");
      let targetDataPath = targetCell.dataPath;
      if (pathSegments[pathSegments.length - 2] === "dataValues") {
        pathSegments.splice(-2, 2);
        targetDataPath = pathSegments.join("/");
      }
      newBodyData = mutateByPath(newBodyData, `${targetDataPath}/dimension`, action.height);
    }
  }

  return {
    ...state,
    bodyData: newBodyData,
    scaffoldModel: newScaffoldModel,
    changed: true,
  };
};
